import { useCallback, useLayoutEffect, useState } from 'react';
/**
 * Hook that manages presence and animation state for enter/exit animations.
 *
 * State transitions:
 *
 * | Trigger                                      | Current State | Next State   | Side Effects        |
 * | -------------------------------------------- | ------------- | ------------ | ------------------- |
 * | `isVisible` becomes `true` + `!isPresent`    | idle          | enter        | `isPresent = true`  |
 * | Animation ends                               | enter         | idle         | -                   |
 * | `isVisible` becomes `false` + `isPresent`    | idle          | exit         | -                   |
 * | Animation ends                               | exit          | (unmounted)  | `isPresent = false` |
 *
 * @param isVisible - Controls whether element should be visible
 * @returns Presence state, animation state, and transition handler
 */
export function useMotionPresence(isVisible) {
    const [isPresent, setIsPresent] = useState(isVisible);
    const [animationState, setAnimationState] = useState(isVisible ? 'enter' : 'idle');
    const [hasBeenIdle, setHasBeenIdle] = useState(false);
    // Mount the element when isVisible becomes true
    // useLayoutEffect ensures this runs synchronously before paint
    useLayoutEffect(() => {
        if (isVisible) {
            if (!isPresent) {
                // Only trigger enter animation when mounting
                // @eslint-disable-next-line react-hooks/set-state-in-effect TODO: Fix this
                setIsPresent(true);
                setAnimationState('enter');
            }
        }
        else if (isPresent) {
            // Start exit animation
            setAnimationState('exit');
        }
    }, [isVisible, isPresent]);
    useLayoutEffect(() => {
        if (animationState === 'idle' && !hasBeenIdle) {
            setHasBeenIdle(true);
        }
    }, [animationState, hasBeenIdle]);
    const handleAnimationEnd = useCallback(() => {
        if (animationState === 'enter') {
            setAnimationState('idle');
        }
        else if (animationState === 'exit') {
            setIsPresent(false);
        }
    }, [animationState]);
    const isInitialEnter = animationState === 'enter' && !hasBeenIdle;
    return {
        isPresent,
        animationState,
        isInitialEnter,
        handleAnimationEnd,
    };
}
//# sourceMappingURL=useMotionPresence.js.map